import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Mail, MapPin, Phone } from 'lucide-react';
import { portfolioData } from '../data/mock';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.1, ease: [0.25, 1, 0.5, 1] }
  })
};

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setStatus('sent');
        setForm({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setStatus(''), 3000);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const { contact } = portfolioData;

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 lg:py-40 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="md:col-span-5"
          >
            <motion.p variants={fadeUp} custom={0} className="text-xs md:text-sm uppercase tracking-[0.3em] font-medium text-white/40 mb-4">
              Contact
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="font-serif text-4xl sm:text-5xl md:text-6xl font-light tracking-tight leading-tight text-white mb-8">
              Let's Create<br />Together
            </motion.h2>

            <motion.div variants={fadeUp} custom={2} className="space-y-6 text-sm font-light">
              <a href={`mailto:${contact.email}`} data-testid="contact-email" className="flex items-center gap-3 text-white/50 hover:text-white transition-colors duration-300">
                <Mail className="w-4 h-4" />
                {contact.email}
              </a>
              <a href={`tel:${contact.phone}`} data-testid="contact-phone" className="flex items-center gap-3 text-white/50 hover:text-white transition-colors duration-300">
                <Phone className="w-4 h-4" />
                {contact.phone}
              </a>
              <div className="flex items-center gap-3 text-white/50" data-testid="contact-location">
                <MapPin className="w-4 h-4" />
                {contact.location}
              </div>
            </motion.div>

            <motion.div variants={fadeUp} custom={3} className="flex gap-4 mt-8">
              {Object.entries(contact.social).map(([name, url]) => (
                <a
                  key={name}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`social-link-${name}`}
                  className="text-xs uppercase tracking-[0.2em] text-white/30 hover:text-white transition-colors duration-300 link-underline"
                >
                  {name}
                </a>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="md:col-span-6 md:col-start-7"
          >
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
              {['name', 'email', 'subject'].map((field, i) => (
                <motion.div key={field} variants={fadeUp} custom={i}>
                  <input
                    type={field === 'email' ? 'email' : 'text'}
                    name={field}
                    value={form[field]}
                    onChange={handleChange}
                    required
                    data-testid={`contact-input-${field}`}
                    placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                    className="w-full bg-transparent border-b border-white/15 focus:border-white py-3 text-base font-light text-white placeholder:text-white/25 outline-none transition-colors duration-300"
                  />
                </motion.div>
              ))}
              <motion.div variants={fadeUp} custom={3}>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  data-testid="contact-input-message"
                  placeholder="Your message"
                  className="w-full bg-transparent border-b border-white/15 focus:border-white py-3 text-base font-light text-white placeholder:text-white/25 outline-none resize-none transition-colors duration-300"
                />
              </motion.div>
              <motion.div variants={fadeUp} custom={4}>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={status === 'sending'}
                  className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.2em] font-medium text-white/80 hover:text-white transition-colors duration-300 disabled:opacity-50"
                >
                  <span className="w-10 h-10 border border-white/20 group-hover:border-white/50 flex items-center justify-center transition-colors duration-300">
                    <Send className="w-4 h-4" />
                  </span>
                  {status === 'sending' ? 'Sending...' : status === 'sent' ? 'Message Sent!' : 'Send Message'}
                </button>
                {status === 'error' && (
                  <p className="text-xs text-red-400/80 mt-3" data-testid="contact-error">Something went wrong. Please try again.</p>
                )}
              </motion.div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
